import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, MapPin, Utensils, Clock, Phone } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Restaurant {
  rest_id: number;
  restaurant_name: string;
  cuisine_type: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string | null;
  location_id: number;
  chain_id: number;
  has_menu: boolean;
  menu_items_count: number;
}

const CustomerBrowse = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedCuisine, setSelectedCuisine] = useState<string>('');
  const [cities, setCities] = useState<string[]>([]);
  const [cuisineTypes, setCuisineTypes] = useState<string[]>([]);

  useEffect(() => {
    fetchRestaurants();
  }, [selectedCity, selectedCuisine]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCity) params.append('city', selectedCity);
      if (selectedCuisine) params.append('cuisine_type', selectedCuisine);
      
      const response = await apiClient.get(`/public/restaurants${params.toString() ? '?' + params.toString() : ''}`);
      setRestaurants(response.data);
      
      // Extract unique cities and cuisines for filters
      const uniqueCities = [...new Set(response.data.map((r: Restaurant) => r.city))].sort();
      const uniqueCuisines = [...new Set(response.data.map((r: Restaurant) => r.cuisine_type))].sort();
      setCities(uniqueCities);
      setCuisineTypes(uniqueCuisines);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchRestaurants();
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/public/search/restaurants?q=${encodeURIComponent(searchQuery)}`);
      const restaurantIds = response.data.results.map((r: any) => r.rest_id);
      
      // Filter the current restaurants to show only search results
      setRestaurants(restaurants.filter(r => restaurantIds.includes(r.rest_id)));
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1f4ed1] to-[#2256e5] text-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-4">Find Your Perfect Meal</h1>
          <p className="text-xl text-white/80">
            Discover amazing restaurants in your area
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="container mx-auto px-4 -mt-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  placeholder="Search restaurants or cuisine..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger>
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedCuisine} onValueChange={setSelectedCuisine}>
              <SelectTrigger>
                <SelectValue placeholder="All Cuisines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Cuisines</SelectItem>
                {cuisineTypes.map(cuisine => (
                  <SelectItem key={cuisine} value={cuisine}>{cuisine}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p>
              <span className="font-medium text-green-600">
                {restaurants.length} active restaurants
              </span> 
              {' '}ready to serve you
              <span className="ml-2 text-gray-400">
                (Temporarily closed locations are hidden)
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Restaurant Grid */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-2 text-gray-600">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1f4ed1]"></div>
              Loading restaurants...
            </div>
          </div>
        ) : restaurants.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 text-lg">
              No active restaurants found matching your criteria
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {restaurants.map((restaurant, index) => (
              <motion.div
                key={restaurant.rest_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {restaurant.restaurant_name}
                      </h3>
                      {restaurant.location_id > 1 && (
                        <span className="text-sm text-gray-500">
                          Location #{restaurant.location_id}
                        </span>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                      <Utensils className="h-3 w-3" />
                      {restaurant.cuisine_type}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{restaurant.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="ml-6">
                        {restaurant.city}, {restaurant.state} {restaurant.zip_code}
                      </span>
                    </div>
                    {restaurant.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{restaurant.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="text-sm">
                      {restaurant.has_menu ? (
                        <span className="text-green-600 font-medium">
                          {restaurant.menu_items_count} menu items
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          Menu coming soon
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="bg-[#1f4ed1] hover:bg-[#2256e5]"
                      disabled={!restaurant.has_menu}
                    >
                      View Menu
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerBrowse;